import { ReactElement } from "react";
import { useDependency } from "../../../../contexts/DependencyContext";
import { useTimeMachine } from "../../../../contexts/TimeMachineContext"
import { useRecurrentAsyncCall } from "../../../../hooks/UseAsyncCall";
import { IWeatherData } from "../../../../services/http/weatherdata/models/IWeatherData";
import { WeatherData } from "../../../../services/http/weatherdata/models/WeatherData";
import { GetWeatherParameters } from "../../../../services/http/weatherdata/models/GetWeatherParameters";
import { WeatherDataService } from "../../../../services/http/weatherdata/WeatherDataService";
import { ComponentModelService } from "../../../../services/ComponentModelService";
import IndicatorNumber from "../../IndicatorNumber";
import Spinner from "../../Spinner";
import NoData from "../../NoData";
import MainInfo from "./MainInfo";
import Current from "./Current";
import styles from './Weather.module.scss';

interface WeatherProps {
    componentId: number;
}

async function loadData(service: WeatherDataService, componentId: number, date: Date): Promise<IWeatherData>
{
    const parameters = new GetWeatherParameters();
    parameters.componentId = componentId;
    parameters.date = date ?? new Date();

    return await service.getForComponent(parameters);
}

const Weather = (props: WeatherProps): ReactElement =>
{
    const service = useDependency(WeatherDataService);
    const { component } = useDependency(ComponentModelService).componentModel;
    const timeMachineDate = useTimeMachine();

    const { loading, content } = useRecurrentAsyncCall(parseInt(process.env.REACT_APP_CHART_REFRESH_RATE ?? "300000"), loadData, service, props.componentId, timeMachineDate);
    const weatherData = content || new WeatherData();

    if (loading)
        return <Spinner caption="Loading widget..." />;

    return weatherData
        ? <div className={styles.wrapper}>
            <MainInfo {...weatherData} />
            <Current weatherData={weatherData} location={component.location} />

            <div className={styles.indicators}>
                <IndicatorNumber label="Humidity" unitOfMeasure="%" value={weatherData.humidity} />
                <IndicatorNumber label="Pressure" unitOfMeasure="hpa" value={weatherData.pressure} />
                <IndicatorNumber label="Clouds" unitOfMeasure="%" value={weatherData.cloudiness} />
                <IndicatorNumber label="Wind" unitOfMeasure="mph" value={weatherData.windSpeed} />
            </div>
        </div>
        : <NoData message="No weather data available" />;
}

export default Weather;